/**
 * usePayment — shared Cashfree payment + booking-confirm helper.
 *
 * Provides two utilities used by prepaid booking, pay-at-exit, and
 * wallet recharge:
 *
 *  launchBookingPayment(sessionId, bookingId, returnPath, token)
 *    – persists the pending booking reference in sessionStorage,
 *      then redirects to Cashfree.  On Cashfree's return URL,
 *      callers retrieve the reference with getPendingPayment() and
 *      call completeBookingPayment().
 *
 *  completeBookingPayment(orderId, bookingId, token)
 *    – calls api/booking/make-booking/ with the Cashfree order_id
 *      and returns the make-booking response.  Clears storage on
 *      success.
 *
 *  getPendingPayment()   – read the pending entry from storage.
 *  clearPendingPayment() – remove it (call after success or on error).
 *
 *  useCashfreeReturn(returnPath, onSuccess, onError)
 *    – React hook.  On mount it looks for ?order_id= in the URL.
 *      If found it reads the pending booking from storage and calls
 *      completeBookingPayment, then onSuccess(body) / onError(err).
 *      Designed to be called at the top of a page component.
 *
 * Wallet recharge does NOT call make-booking; it calls verify-recharge-wallet
 * instead.  WalletPage handles that itself but reuses launchCashfreeCheckout
 * from useCashfree.js (unchanged).
 */

import { useEffect, useRef } from "react";
import { launchCashfreeCheckout } from "./useCashfree";
import * as b2cApi from "../api/b2cApi";

var STORAGE_KEY = "citizen_pending_payment";

/** Persist booking reference before the Cashfree redirect. */
export function storePendingPayment(bookingId, returnPath) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ bookingId: String(bookingId), returnPath: returnPath || "/" })
    );
  } catch (_) {}
}

/** Read the pending payment entry (returns null if missing). */
export function getPendingPayment() {
  try {
    var raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/** Remove the pending payment entry. */
export function clearPendingPayment() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

/**
 * Persist pending booking, build the return URL, then redirect to Cashfree.
 *
 * @param {string} sessionId  – cashfree_token / payment_session_id
 * @param {string} bookingId  – booking_id from confirm-booking-v2 or CHECKOUT
 * @param {string} returnPath – e.g. "/citizen/book/summary" or "/citizen/booking/42"
 * @returns {Promise<void>}
 */
export function launchBookingPayment(sessionId, bookingId, returnPath) {
  storePendingPayment(bookingId, returnPath);
  var returnUrl =
    window.location.origin + returnPath + "?order_id={order_id}";
  return launchCashfreeCheckout(sessionId, returnUrl);
}

/**
 * Verify a Cashfree payment by calling make-booking.
 * Clears the pending storage entry on success.
 *
 * @param {string} orderId    – value of ?order_id= from Cashfree return URL
 * @param {string} bookingId  – booking_id stored before the redirect
 * @param {string} token      – citizen access_token
 * @returns {Promise<object>} – make-booking API response
 */
export function completeBookingPayment(orderId, bookingId, token) {
  return b2cApi
    .makeBooking({ booking_id: bookingId, payment_reference_id: orderId }, token)
    .then(function (body) {
      clearPendingPayment();
      return body;
    });
}

/**
 * React hook: detect Cashfree return (?order_id=) and complete booking.
 *
 * @param {boolean}          active     – whether this page should handle returns
 * @param {string}           token      – citizen access_token
 * @param {function(body)}   onSuccess  – called with make-booking response
 * @param {function(Error)}  onError    – called on API failure
 * @param {function}         setBusy    – optional setter (true while in-flight)
 */
export function useCashfreeReturn({ active, token, onSuccess, onError, setBusy }) {
  // Use a ref so we only handle one return per mount even in StrictMode.
  var handledRef = useRef(false);

  useEffect(function () {
    if (!active || handledRef.current) return;
    var qs = new URLSearchParams(window.location.search);
    var orderId = qs.get("order_id") || qs.get("orderId");
    if (!orderId) return;

    var pending = getPendingPayment();
    if (!pending || !pending.bookingId) {
      // Lost storage — surface the order_id so support can look it up.
      onError(
        Object.assign(
          new Error(
            "Payment received but booking reference was lost. " +
            "Contact support with payment ID: " + orderId
          ),
          { orphanOrderId: orderId }
        )
      );
      return;
    }

    handledRef.current = true;
    if (setBusy) setBusy(true);

    completeBookingPayment(orderId, pending.bookingId, token)
      .then(function (body) {
        onSuccess(body);
      })
      .catch(function (err) {
        // Don't clear pending on failure so the user can retry.
        handledRef.current = false;
        onError(err);
        if (setBusy) setBusy(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
